import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CHAT_NUDGE_DELAY, CHAT_NUDGE_KEY, chatEnabled, closeAiChat, getChatButtonVariant, loadChatWidget, openAiChat, trackChat, type ChatButtonVariant } from '@/lib/ai-chat';
import { claimChatAbView, logChatAb } from '@/lib/chat-ab-log';
import avatarAsset from '@/assets/arno-chat-avatar.webp.asset.json';

/** Styles live inside GHL's open shadow root; never hide the panel with the launcher. */
const widgetStyles = `
  .lc_text-widget--bubble, .lc_text-widget--prompt { display: none !important; }
  @media (max-width: 767px) {
    .lc_text-widget--active { position: fixed !important; inset: 0 !important; width: 100% !important; height: 100dvh !important; z-index: 10000 !important; }
    .lc_text-widget--box.active { position: fixed !important; inset: 0 !important; width: 100% !important; height: 100dvh !important; max-height: none !important; border-radius: 0 !important; margin: 0 !important; }
  }
`;

export function FloatingAiChat() {
  const { pathname } = useLocation();
  const enabled = chatEnabled(pathname);
  const [nudge, setNudge] = useState(false);
  const [opening, setOpening] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [variant, setVariant] = useState<ChatButtonVariant>();
  const viewTracked = useRef(false);
  const launcher = useRef<HTMLDivElement>(null);

  useEffect(() => { setVariant(getChatButtonVariant()); }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('ai-chat-excluded', !enabled);
    if (!enabled) { closeAiChat(); setNudge(false); return; }
    loadChatWidget();
    const sync = () => {
      document.querySelectorAll('chat-widget').forEach((host) => {
        const root = host.shadowRoot;
        if (!root) return;
        if (!root.querySelector('[data-taoh-chat-style]')) {
          const style = document.createElement('style');
          style.dataset.taohChatStyle = '';
          style.textContent = widgetStyles;
          root.appendChild(style);
        }
        host.setAttribute('data-taoh-styled', '');
        setChatOpen(host.getAttribute('data-active') === 'true');
      });
    };
    sync();
    const timer = window.setInterval(sync, 100);
    return () => window.clearInterval(timer);
  }, [enabled]);

  useEffect(() => {
    setNudge(false);
    if (!enabled) return;
    try { if (sessionStorage.getItem(CHAT_NUDGE_KEY)) return; } catch { /* storage can be disabled */ }
    const timer = window.setTimeout(() => {
      try { sessionStorage.setItem(CHAT_NUDGE_KEY, 'seen'); } catch { /* still offer chat */ }
      setNudge(true);
    }, CHAT_NUDGE_DELAY);
    return () => window.clearTimeout(timer);
  }, [pathname, enabled]);

  useEffect(() => {
    const el = launcher.current;
    if (!el || !enabled) return;
    const clearControls = () => {
      el.style.removeProperty('--ai-chat-lift');
      if (!/^\/vsl(?:-[ab])?\/?$/.test(pathname)) return;
      const box = el.getBoundingClientRect();
      const nudgeBox = el.querySelector('.ai-chat-nudge')?.getBoundingClientRect();
      const top = nudgeBox?.top ?? box.top;
      const left = Math.min(box.left, nudgeBox?.left ?? box.left);
      let lift = 0;
      document.querySelectorAll('.vsl-qualify-card button, .vsl-qualify-card input, .vsl-cta, [id^="vidalytics_embed_"]').forEach((target) => {
        const rect = target.getBoundingClientRect();
        if (rect.width && rect.height && rect.right > left && rect.left < box.right && rect.bottom > top && rect.top < box.bottom) {
          lift = Math.max(lift, box.bottom - rect.top + 12);
        }
      });
      if (lift) el.style.setProperty('--ai-chat-lift', `${lift}px`);
    };
    clearControls();
    window.addEventListener('scroll', clearControls, { passive: true });
    window.addEventListener('resize', clearControls);
    const observer = new ResizeObserver(clearControls);
    const page = document.querySelector('.vsl-page');
    if (page) observer.observe(page);
    return () => { window.removeEventListener('scroll', clearControls); window.removeEventListener('resize', clearControls); observer.disconnect(); };
  }, [pathname, enabled, nudge, variant]);

  useEffect(() => {
    if (!enabled || !variant || viewTracked.current) return;
    viewTracked.current = true;
    trackChat('ai_chat_button_view', variant);
  }, [enabled, variant]);

  useEffect(() => {
    if (enabled && variant && claimChatAbView(pathname)) logChatAb('button_view', variant);
  }, [enabled, variant, pathname]);

  const handleOpen = async () => {
    setNudge(false);
    try { sessionStorage.setItem(CHAT_NUDGE_KEY, 'seen'); } catch { /* optional storage */ }
    if (variant) { trackChat('ai_chat_button_click', variant); logChatAb('button_click', variant); }
    setOpening(true);
    try {
      const opened = await openAiChat(variant);
      if (opened && variant) logChatAb('chat_open', variant);
      if (!opened) toast.error('Chat is still loading. Please try again.');
    }
    catch { toast.error('Chat could not open. Please try again.'); }
    finally { setOpening(false); }
  };

  if (!enabled || !variant) return null;
  return (
    <div ref={launcher} className="ai-chat-launcher fixed bottom-5 right-5 z-40" hidden={chatOpen}>
      {nudge && <div role="status" className="ai-chat-nudge absolute bottom-full right-0 mb-3 flex w-64 max-w-[calc(100vw-2.5rem)] items-start gap-2 rounded-lg border border-border bg-background p-3 text-sm font-medium text-foreground shadow-lg">
        <p>Got 30 seconds? See if we're a fit.</p>
        <Button variant="ghost" size="icon" aria-label="Dismiss chat invitation" className="h-6 w-6 shrink-0" onClick={() => setNudge(false)}><X /></Button>
      </div>}
      <button type="button" disabled={opening} onClick={handleOpen} aria-label="Ask Arno's AI" aria-busy={opening} className="block h-16 w-16 rounded-full transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:h-[72px] md:w-[72px]">
        <img src={avatarAsset.url} alt="" width={72} height={72} className="h-full w-full object-contain" />
      </button>
    </div>
  );
}